import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  TextField,
  InputAdornment,
  Checkbox,
  FormControl,
  Select,
  MenuItem,
  ListItemText,
  Tooltip,
} from "@mui/material";
import Lottie from "lottie-react";
import loaderAnimation from "../../assets/loader/loader.json";
import { Search as SearchIcon, Plus as PlusIcon } from "lucide-react";
import CloseIcon from "@mui/icons-material/Close";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { placeholderText } from "../../constants/colors";
import { getCurrentUser } from "../../utils/auth";
import {
  searchSubscribers,
  createMembers,
  fetchPrograms,
} from "../../api/subscriber";

// The employee PK of the user submitting the add-patient request, sent as
// each item's assigneeId so a brand-new member is assigned to whoever added
// them (an existing member found by subscriberNum+personCode keeps its
// current assignee - see MemberServiceImpl#toNewMember).
const getCurrentAssigneeId = () => {
  const currentUser = getCurrentUser();
  return currentUser?.id ?? currentUser?.employeeId ?? currentUser?.assigneeId;
};

// The per-patient program picker only ever offers these programs, in this
// order. Options are still resolved (id + name) from the programs endpoint by
// matching these names - this list only controls which of the endpoint's
// programs are shown.
const ALLOWED_PROGRAM_NAMES = [
  "Women's Health Program",
  "Weight Management",
  "Diabetes",
];

// Only Women's Health Program is gender-restricted.
const WOMENS_HEALTH_PROGRAM_NAME = "Women's Health Program";

// RX_SUBSCRIBER.GENDER code for female; only these subscribers are selectable
// for Women's Health Program.
const FEMALE_GENDER_CODE = new Set(["2", "F"]);

// RX_SUBSCRIBER.GENDER code labels, for display in the results table.
const GENDER_LABELS = {
  1: "Male",
  2: "Female",
  M: "Male",
  F: "Female",

  0: "Unknown",
};

// Map the single free-text search box to the API's separate params:
//   - contains a digit -> treat as a member/subscriber id
//   - contains a space -> treat as "First Last"
//   - single word      -> treat as a last name
// Returns null when the query is empty.
const buildSearchParams = (raw) => {
  const q = (raw || "").trim();
  if (!q) return null;
  if (/\d/.test(q)) return { subscriberNum: q };
  const parts = q.split(/\s+/);
  if (parts.length > 1) {
    return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
  }
  return { lastName: q };
};

/**
 * Classifies a create-members response against the (member, programs) we
 * submitted. Each attempt is { tdmPatientId, subscriberNum, patientName,
 * programIds }.
 *
 * Membership in the response's `enrolled` list is the definitive signal that a
 * person was enrolled - an enrolled member may come back with a different (or
 * null) tdmPatientId than the searched subscriber (an existing HALO member is
 * reused), so we match `enrolled` by tdmPatientId OR subscriberNum. If the
 * member was not enrolled, its non-failed programs are "not enrolled" (the
 * endpoint returned neither an enrollment nor a failure for them - no valid
 * claims). Returns the raw `enrolled`/`failed` plus derived
 * `successes`/`notEnrolled` lists (entries carry programIds, resolved to names
 * at render).
 */
const classifyCreateResponse = (response, attempts) => {
  const enrolled = Array.isArray(response?.enrolled) ? response.enrolled : [];
  const rawFailed = Array.isArray(response?.failed) ? response.failed : [];

  const failedProgramsByPatient = rawFailed.reduce((acc, f) => {
    const key = String(f.tdmPatientId);
    (acc[key] || (acc[key] = new Set())).add(String(f.programId));
    return acc;
  }, {});
  const enrolledTdmIds = new Set(
    enrolled
      .map((m) => (m?.tdmPatientId != null ? String(m.tdmPatientId) : null))
      .filter(Boolean),
  );
  const enrolledSubscriberNums = new Set(
    enrolled
      .map((m) => (m?.subscriberNum != null ? String(m.subscriberNum) : null))
      .filter(Boolean),
  );
  const isEnrolled = (attempt) =>
    enrolledTdmIds.has(String(attempt.tdmPatientId)) ||
    (attempt.subscriberNum != null &&
      enrolledSubscriberNums.has(String(attempt.subscriberNum)));

  const successes = [];
  const notEnrolled = [];
  attempts.forEach((a) => {
    const failedSet =
      failedProgramsByPatient[String(a.tdmPatientId)] || new Set();

    if (isEnrolled(a)) {
      // An enrolled member is always reported as enrolled (fall back to the
      // submitted programs if every one happened to also be listed as failed).
      const remaining = (a.programIds || []).filter(
        (pid) => !failedSet.has(String(pid)),
      );
      const programIds = remaining.length > 0 ? remaining : a.programIds || [];
      if (programIds.length > 0) {
        successes.push({
          tdmPatientId: a.tdmPatientId,
          patientName: a.patientName,
          programIds,
        });
      }
      return;
    }

    // Not enrolled. A member with any failure (even a member-level one with a
    // null programId) is owned by the failure/prompt panels, which read
    // `failed` directly - so only members the endpoint returned neither an
    // enrollment nor a failure for are "not enrolled" (no valid claims).
    if (failedSet.size > 0) return;
    const programIds = a.programIds || [];
    if (programIds.length > 0) {
      notEnrolled.push({
        tdmPatientId: a.tdmPatientId,
        patientName: a.patientName,
        programIds,
      });
    }
  });

  // The endpoint can report the same (member, program) pair in both
  // `enrolled` and `failed` (e.g. a retry that succeeds may still echo a
  // stale failure for the program it just added) - enrollment wins, so drop
  // any failure whose pair was actually resolved to a success rather than
  // showing a contradictory "could not be added" message for it.
  const successfulPairs = new Set();
  successes.forEach((s) => {
    s.programIds.forEach((pid) =>
      successfulPairs.add(`${s.tdmPatientId}:${String(pid)}`),
    );
  });
  const failed = rawFailed.filter(
    (f) => !successfulPairs.has(`${f.tdmPatientId}:${String(f.programId)}`),
  );

  return { enrolled, failed, successes, notEnrolled };
};

const AddPatientModal = ({ open, onClose, onAdd }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  // Row ids (checkbox-selected patients) included in the Add action.
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState("");
  // Per-(patient, program) failures returned by the create-members endpoint,
  // each shaped { tdmPatientId, programId, reason }.
  const [addFailures, setAddFailures] = useState([]);
  // Enrollment successes derived from the create-members response, each shaped
  // { tdmPatientId, patientName, programNames }.
  const [addSuccesses, setAddSuccesses] = useState([]);
  // Selected patients the endpoint neither created nor listed as a failure -
  // i.e. not enrolled because there were no valid claims for the program. Same
  // shape as successes: { tdmPatientId, patientName, programNames }.
  const [addNotEnrolled, setAddNotEnrolled] = useState([]);
  // True once a create-members response has come back; switches the modal to its status view.
  const [submitted, setSubmitted] = useState(false);
  // Whether anything was actually persisted, so we know whether to refresh the parent on close.
  const [hasCreated, setHasCreated] = useState(false);
  // Program options ({ id, name }[]) resolved from the programs endpoint,
  // restricted to ALLOWED_PROGRAM_NAMES.
  const [programs, setPrograms] = useState([]);
  // Each checked row picks its own program(s) via its own dropdown. Shaped
  // { [rowId]: string[] } (program ids as strings, matching Select's value
  // type).
  const [programSelections, setProgramSelections] = useState({});

  // Load the program options whenever the modal opens.
  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    fetchPrograms()
      .then((list) => {
        const byName = new Map(
          (Array.isArray(list) ? list : []).map((p) => [
            (p.name || "").trim().toLowerCase(),
            p,
          ]),
        );
        const allowed = ALLOWED_PROGRAM_NAMES.map((name) =>
          byName.get(name.toLowerCase()),
        ).filter(Boolean);
        if (active) setPrograms(allowed);
      })
      .catch((e) => {
        console.error("Failed to load programs", e);
        if (active) setPrograms([]);
      });
    return () => {
      active = false;
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setResults([]);
      setHasSearched(false);
      setSelectedIds([]);
      setProgramSelections({});
      setLoading(false);
      setError("");
      setAdding(false);
      setAddError("");
      setAddFailures([]);
      setAddSuccesses([]);
      setAddNotEnrolled([]);
      setSubmitted(false);
      setHasCreated(false);
    }
  }, [open]);

  const handleSearch = async () => {
    const params = buildSearchParams(query);
    if (!params) return;
    setLoading(true);
    setError("");
    setSelectedIds([]);
    setProgramSelections({});
    setAddError("");
    setAddFailures([]);
    setAddSuccesses([]);
    setAddNotEnrolled([]);
    setSubmitted(false);
    setHasCreated(false);
    try {
      const data = await searchSubscribers(params);
      // Map MemberSearchResultDTO into the shape used by the results table.
      // Use the row index as the selection/key id: memberId is not guaranteed
      // unique (or present) in the response, which would otherwise make every
      // row share the same id and select together.
      const normalized = data.map((r, index) => ({
        id: index,
        memberId: r.memberId,
        tdmPatientId: r.tdmPatientId,
        firstName: r.firstName,
        lastName: r.lastName,
        subscriberNumber: r.subscriberNum,
        personCode: r.personCode,
        gender: r.gender,
        hasHaloProgram: r.hasHaloProgram,
      }));
      setResults(normalized);
    } catch (e) {
      console.error("Subscriber search failed", e);
      setResults([]);
      setError("Unable to search patients. Please try again.");
    } finally {
      setHasSearched(true);
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    // Multi selection: checking a row adds it to the Add action; unchecking
    // removes it. Multiple patients can be selected at once, each with their
    // own program(s) via their own dropdown.
    setAddError("");
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleAdd = async () => {
    // Each checked patient is enrolled in the program(s) picked in their own
    // row, always with the program's default drug - there's no valid-claims
    // check to retry against, so this is the only create-members call.
    const checked = results.filter((r) => selectedIds.includes(r.id));
    const missingPrograms = checked.filter(
      (r) => (programSelections[r.id] || []).length === 0,
    );
    if (missingPrograms.length > 0) {
      const names = missingPrograms
        .map((r) => [r.firstName, r.lastName].filter(Boolean).join(" "))
        .join(", ");
      setAddError(`Select at least one program for ${names}.`);
      return;
    }

    const selected = checked.map((r) => ({
      patient: r,
      programIds: (programSelections[r.id] || []).map(Number),
    }));

    const assigneeId = getCurrentAssigneeId();
    const outreachDtos = selected.map(({ patient, programIds }) => ({
      tdmPatientId: Number(patient.tdmPatientId),
      programIds,
      useDefaultDrug: true,
      assigneeId,
    }));

    setAdding(true);
    setAddError("");
    setAddFailures([]);
    setAddSuccesses([]);
    setAddNotEnrolled([]);
    try {
      const response = await createMembers(outreachDtos);
      const attempts = selected.map(({ patient, programIds }) => ({
        tdmPatientId: patient.tdmPatientId,
        subscriberNum: patient.subscriberNumber,
        patientName: [patient.firstName, patient.lastName]
          .filter(Boolean)
          .join(" "),
        programIds,
      }));

      const { enrolled, failed, successes, notEnrolled } =
        classifyCreateResponse(response, attempts);

      // Switch to the status view. The parent list is refreshed on close (only
      // when something was persisted), so the user reads the result first.
      setAddSuccesses(successes);
      setAddNotEnrolled(notEnrolled);
      setAddFailures(failed);
      setHasCreated(enrolled.length > 0);
      setSubmitted(true);
    } catch (e) {
      console.error("Create members failed", e);
      setAddError("Unable to add patients. Please try again.");
    } finally {
      setAdding(false);
    }
  };

  // Close the modal, refreshing the parent list first if anything was persisted.
  const handleClose = () => {
    if (hasCreated) onAdd?.();
    onClose();
  };

  // Resolve a failure's tdmPatientId back to the patient's display name, and
  // its programId to the program name, for a human-readable message.
  const patientNameByTdmId = (tdmPatientId) => {
    const match = results.find(
      (r) => String(r.tdmPatientId) === String(tdmPatientId),
    );
    return match
      ? [match.firstName, match.lastName].filter(Boolean).join(" ")
      : `Patient ${tdmPatientId}`;
  };

  const programNameById = (programId) => {
    const match = programs.find((p) => String(p.id) === String(programId));
    return match ? match.name : `Program ${programId}`;
  };

  // Failures shown in their own panel: at most one message per member (the
  // first entry wins) so a member with several failed programs doesn't
  // produce a wall of near-duplicate lines.
  const seenFailureTdmIds = new Set();
  const visibleFailures = addFailures.filter((f) => {
    const key = String(f.tdmPatientId);
    if (seenFailureTdmIds.has(key)) return false;
    seenFailureTdmIds.add(key);
    return true;
  });
  const visibleNotEnrolled = addNotEnrolled;

  // Matches the column-gutter approach used by ClinicalHistoryModal's
  // <TableCell>s (consistent horizontal padding via MUI's default cell
  // padding) - these rows use plain flex Typography instead of a table, so
  // the same gutter has to be applied explicitly. Truncating with an
  // ellipsis (rather than letting a long value wrap or overflow) is what
  // keeps that gutter consistent regardless of content length.
  const truncatedColumn = {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    pr: 2,
  };
  const columnStyles = {
    checkbox: { flex: "0 0 40px" },
    patientName: { flex: 1, minWidth: 0, ...truncatedColumn },
    patientId: { flex: 1, minWidth: 0, ...truncatedColumn },
    // Member ID gets a fixed, non-shrinking width instead of truncating -
    // the modal is sized wide enough (see maxWidth below) for it to always
    // display in full, with a consistent gutter before Person Code.
    subscriberNumber: { flex: "0 0 160px", pr: 2 },
    personCode: { flex: "0 0 90px" },
    gender: { flex: "0 0 70px" },
    programs: { flex: "0 0 240px" },
  };

  const getButtonText = () => {
    if (submitted) return "Close";
    if (adding) return "Adding...";
    return "Add";
  };
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth={false}
      slotProps={{
        paper: {
          sx: {
            width: { xs: "95%", md: "1100px" },
            maxWidth: "90vw",
            borderRadius: "16px",
            p: { xs: "16px", md: "24px" },
            backgroundColor: "#F7F8FB",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 0,
          m: 0,
          width: "100%",
          height: "40px",
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "20px",
            lineHeight: "30px",
            color: "#000000",
          }}
        >
          Add Patient
        </Typography>
        <IconButton onClick={handleClose} aria-label="Close add patient dialog">
          <CloseIcon sx={{ color: placeholderText }} />
        </IconButton>
      </DialogTitle>

      {!submitted && (
        <TextField
          fullWidth
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void handleSearch();
            }
          }}
          placeholder="Search patient by member id"
          size="small"
          disabled={loading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon size={18} color="#9F9DB6" />
              </InputAdornment>
            ),
          }}
          sx={{
            mt: 2,
            "& .MuiOutlinedInput-root": {
              height: "45px",
              borderRadius: "8px",
              backgroundColor: "#fff",
              "& fieldset": { borderColor: "#E0E0E0" },
              "&:hover fieldset": { borderColor: "#BDBDBD" },
              "&.Mui-focused fieldset": { borderColor: "#3f51b5" },
            },
          }}
        />
      )}

      {!submitted && (
        <Typography sx={{ fontSize: "13px", color: placeholderText, mt: 1 }}>
          Patients added here are enrolled using each program's default drug.
        </Typography>
      )}

      {error && (
        <Typography sx={{ fontSize: "14px", color: "#D32F2F", mt: 2 }}>
          {error}
        </Typography>
      )}

      {loading && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100px"
          mt={3}
        >
          <Lottie
            animationData={loaderAnimation}
            loop
            style={{ width: 120, height: 120 }}
          />
        </Box>
      )}

      {!loading && hasSearched && !error && !submitted && (
        <Box mt={3} data-testid="patient-results">
          {results.length === 0 ? (
            <Typography
              sx={{ fontSize: "14px", color: placeholderText, py: 2 }}
            >
              No patients found.
            </Typography>
          ) : (
            <>
              <Box
                display="flex"
                alignItems="center"
                sx={{
                  px: 1,
                  py: 1,
                  borderBottom: "1px solid #E0E0E0",
                }}
              >
                <Box sx={columnStyles.checkbox} />
                <Typography
                  sx={{
                    ...columnStyles.patientName,
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#403C6F",
                  }}
                >
                  Patient Name
                </Typography>
                <Typography
                  sx={{
                    ...columnStyles.patientId,
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#403C6F",
                  }}
                >
                  Patient Id
                </Typography>
                <Typography
                  sx={{
                    ...columnStyles.subscriberNumber,
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#403C6F",
                  }}
                >
                  Member ID
                </Typography>
                <Typography
                  sx={{
                    ...columnStyles.personCode,
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#403C6F",
                  }}
                >
                  Person Code
                </Typography>
                <Typography
                  sx={{
                    ...columnStyles.gender,
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#403C6F",
                  }}
                >
                  Gender
                </Typography>
                <Typography
                  sx={{
                    ...columnStyles.programs,
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#403C6F",
                  }}
                >
                  Programs
                </Typography>
              </Box>

              {results.map((patient) => {
                const patientName = [patient.firstName, patient.lastName]
                  .filter(Boolean)
                  .join(" ");
                const rowProgramIds = programSelections[patient.id] || [];
                const isChecked = selectedIds.includes(patient.id);

                // Reasons a specific program option can't be picked for this
                // patient, keyed by program id (string, matching Select's
                // option values). Kept off the MenuItem's own `disabled` prop
                // - MUI sets pointer-events: none on a disabled item, which
                // would also block the Tooltip below from ever seeing the
                // hover - so selection is blocked in onChange instead.
                const blockedReasonByProgramId = new Map();
                programs.forEach((p) => {
                  const genderBlocked =
                    p.name === WOMENS_HEALTH_PROGRAM_NAME &&
                    !FEMALE_GENDER_CODE.has(String(patient.gender));
                  // hasHaloProgram is the list of program ids this
                  // subscriber+personCode already has an opportunity for -
                  // that specific program can't be re-added.
                  const alreadyHasProgram =
                    Array.isArray(patient.hasHaloProgram) &&
                    patient.hasHaloProgram.some(
                      (existingId) => String(existingId) === String(p.id),
                    );
                  if (alreadyHasProgram) {
                    blockedReasonByProgramId.set(
                      String(p.id),
                      "Patient is already enrolled in this program.",
                    );
                  } else if (genderBlocked) {
                    blockedReasonByProgramId.set(
                      String(p.id),
                      `Can only add female members to ${WOMENS_HEALTH_PROGRAM_NAME}.`,
                    );
                  }
                });

                return (
                  <Box
                    key={patient.id}
                    display="flex"
                    alignItems="center"
                    sx={{
                      px: 1,
                      py: 1,
                      borderBottom: "1px solid #F0F0F0",
                    }}
                  >
                    <Box sx={columnStyles.checkbox}>
                      <Checkbox
                        checked={isChecked}
                        onChange={() => toggleSelect(patient.id)}
                        inputProps={{
                          "aria-label": `Select ${patientName || "patient " + patient.tdmPatientId}`,
                        }}
                        icon={<CheckBoxOutlineBlankIcon />}
                        checkedIcon={<CheckBoxIcon />}
                        sx={{
                          p: 0,
                          color: "#9F9DB6",
                          "&.Mui-checked": { color: "#403C6F" },
                          "&.Mui-checked .MuiSvgIcon-root": {
                            color: "#403C6F",
                          },
                        }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        ...columnStyles.patientName,
                        fontSize: "14px",
                        color: "#10100F",
                      }}
                    >
                      {patientName}
                    </Typography>
                    <Typography
                      sx={{
                        ...columnStyles.patientId,
                        fontSize: "14px",
                        color: "#10100F",
                      }}
                    >
                      {patient.tdmPatientId}
                    </Typography>
                    <Typography
                      sx={{
                        ...columnStyles.subscriberNumber,
                        fontSize: "14px",
                        color: "#10100F",
                      }}
                    >
                      {patient.subscriberNumber}
                    </Typography>
                    <Typography
                      sx={{
                        ...columnStyles.personCode,
                        fontSize: "14px",
                        color: "#10100F",
                      }}
                    >
                      {patient.personCode}
                    </Typography>
                    <Typography
                      sx={{
                        ...columnStyles.gender,
                        fontSize: "14px",
                        color: "#10100F",
                      }}
                    >
                      {GENDER_LABELS[patient.gender] || patient.gender}
                    </Typography>
                    <FormControl
                      size="small"
                      disabled={!isChecked}
                      sx={columnStyles.programs}
                    >
                      <Select
                        multiple
                        displayEmpty
                        value={rowProgramIds}
                        inputProps={{
                          "aria-label": `Programs for ${patientName || "patient " + patient.tdmPatientId}`,
                        }}
                        onChange={(e) => {
                          const { value } = e.target;
                          const nextValues = (
                            typeof value === "string" ? value.split(",") : value
                          ).filter((id) => !blockedReasonByProgramId.has(id));
                          setAddError("");
                          setProgramSelections((prev) => ({
                            ...prev,
                            [patient.id]: nextValues,
                          }));
                        }}
                        renderValue={(selected) =>
                          selected.length === 0
                            ? "Select programs"
                            : programs
                                .filter((p) => selected.includes(String(p.id)))
                                .map((p) => p.name)
                                .join(", ")
                        }
                        sx={{
                          borderRadius: "8px",
                          backgroundColor: "#fff",
                          fontSize: "13px",
                        }}
                      >
                        {programs.map((p) => {
                          const blockedReason = blockedReasonByProgramId.get(
                            String(p.id),
                          );
                          const optionLabel = (
                            <>
                              <Checkbox
                                checked={rowProgramIds.includes(String(p.id))}
                                icon={<CheckBoxOutlineBlankIcon />}
                                checkedIcon={<CheckBoxIcon />}
                                sx={{
                                  p: "4px",
                                  color: "#403C6F",
                                  "&.Mui-checked": {
                                    color: "#403C6F !important",
                                  },
                                  "&.Mui-checked .MuiSvgIcon-root": {
                                    color: "#403C6F !important",
                                  },
                                }}
                              />
                              <ListItemText primary={p.name} />
                            </>
                          );
                          return (
                            <MenuItem
                              key={p.id}
                              value={String(p.id)}
                              // Tooltip injects its own aria-label (matching
                              // the tooltip text) onto the element it wraps,
                              // which would otherwise replace this option's
                              // accessible name entirely. Setting it directly
                              // here takes precedence, so the option is still
                              // announced by its program name too.
                              aria-label={
                                blockedReason
                                  ? `${p.name}. ${blockedReason}`
                                  : undefined
                              }
                              sx={
                                blockedReason
                                  ? { opacity: 0.5, cursor: "not-allowed" }
                                  : undefined
                              }
                            >
                              {blockedReason ? (
                                <Tooltip
                                  title={blockedReason}
                                  placement="right"
                                  arrow
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      width: "100%",
                                    }}
                                  >
                                    {optionLabel}
                                  </Box>
                                </Tooltip>
                              ) : (
                                optionLabel
                              )}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Box>
                );
              })}
            </>
          )}
        </Box>
      )}

      {submitted && (
        <Box mt={3} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {addSuccesses.length === 0 &&
            visibleNotEnrolled.length === 0 &&
            visibleFailures.length === 0 && (
              <Typography sx={{ fontSize: "14px", color: placeholderText }}>
                No changes were made.
              </Typography>
            )}

          {addSuccesses.length > 0 && (
            <Box
              data-testid="add-status-success"
              sx={{
                p: 2,
                borderRadius: "8px",
                border: "1px solid #BBD9BB",
                backgroundColor: "#EAF6EA",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#1E4620",
                  mb: 1,
                }}
              >
                {addSuccesses.length === 1
                  ? "1 patient enrolled:"
                  : `${addSuccesses.length} patients enrolled:`}
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {addSuccesses.map((s) => (
                  <Typography
                    key={s.tdmPatientId}
                    component="li"
                    sx={{ fontSize: "13px", color: "#1E4620", mb: 0.5 }}
                  >
                    <strong>{s.patientName}</strong>
                    {" — "}
                    {s.programIds.map(programNameById).join(", ")}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          {visibleNotEnrolled.length > 0 && (
            <Box
              data-testid="add-status-not-enrolled"
              sx={{
                p: 2,
                borderRadius: "8px",
                border: "1px solid #F0D9A8",
                backgroundColor: "#FDF6E3",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#7A5B00",
                  mb: 1,
                }}
              >
                {visibleNotEnrolled.length === 1
                  ? "1 patient not enrolled:"
                  : `${visibleNotEnrolled.length} patients not enrolled:`}
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {visibleNotEnrolled.map((s) => (
                  <Typography
                    key={s.tdmPatientId}
                    component="li"
                    sx={{ fontSize: "13px", color: "#7A5B00", mb: 0.5 }}
                  >
                    <strong>{s.patientName}</strong>
                    {" — "}
                    {s.programIds.map(programNameById).join(", ")}: no valid
                    claims for the program
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          {visibleFailures.length > 0 && (
            <Box
              data-testid="add-status-failed"
              sx={{
                p: 2,
                borderRadius: "8px",
                border: "1px solid #F5C6C6",
                backgroundColor: "#FDECEC",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#B3261E",
                  mb: 1,
                }}
              >
                {visibleFailures.length === 1
                  ? "1 enrollment could not be added:"
                  : `${visibleFailures.length} enrollments could not be added:`}
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {visibleFailures.map((f, i) => (
                  <Typography
                    key={`${f.tdmPatientId}-${f.programId}-${i}`}
                    component="li"
                    sx={{ fontSize: "13px", color: "#5F2120", mb: 0.5 }}
                  >
                    <strong>{patientNameByTdmId(f.tdmPatientId)}</strong>
                    {" — "}
                    {programNameById(f.programId)}:{" "}
                    {f.reason || "Unknown error."}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}

      {(submitted || selectedIds.length > 0) && (
        <DialogActions
          sx={{
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "10px",
            mt: 1,
          }}
        >
          {addError && (
            <Typography sx={{ fontSize: "14px", color: "#D32F2F", mr: "auto" }}>
              {addError}
            </Typography>
          )}
          <Button
            onClick={submitted ? handleClose : handleAdd}
            variant="contained"
            color="primary"
            disabled={adding}
            startIcon={submitted ? undefined : <PlusIcon size={16} />}
            sx={{
              borderRadius: "52px",
              backgroundColor: "#403C6F",
              color: "#FFFFFF",
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 600,
              px: 3,
              letterSpacing: 0,
              "&:hover": { backgroundColor: "#332F5B" },
              "&:focus-visible": {
                outline: "2px solid #1C6DD0",
                outlineOffset: "2px",
              },
              "&:disabled": {
                backgroundColor: "#D4D4D6",
                color: "#403C6F",
                cursor: "not-allowed",
                opacity: 1,
              },
            }}
          >
            {getButtonText()}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

AddPatientModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAdd: PropTypes.func,
};

export default AddPatientModal;
